import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, rupiah, tanggal, apiError } from "@/lib/api";
import { SiteLayout } from "@/site/SiteLayout";
import { SectionTitle, Loading, Empty } from "@/site/parts";
import { ShoppingCart, Trash2, Search, Phone, Mail, MapPin, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const CART_KEY = "rsh_cart";
const readCart = () => { try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; } };
const writeCart = (c) => localStorage.setItem(CART_KEY, JSON.stringify(c));

export function Toko() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");
  const [cart, setCart] = useState(readCart());
  useEffect(() => {
    api.get("/public/shop", { params: { category: cat || undefined } }).then((r) => setData(r.data)).catch(() => setData({ items: [], categories: [] }));
  }, [cat]);
  const add = (p) => {
    const next = [...cart];
    const f = next.find((x) => x.id === p.id);
    if (f) f.qty += 1; else next.push({ id: p.id, name: p.name, price: p.price, qty: 1 });
    setCart(next); writeCart(next);
    toast.success(`${p.name} ditambahkan ke keranjang`);
  };
  const remove = (id) => { const n = cart.filter((x) => x.id !== id); setCart(n); writeCart(n); };
  const total = cart.reduce((s, x) => s + x.price * x.qty, 0);
  return (
    <SiteLayout>
      <div className="border-b border-[#E5DEC9] bg-white py-14">
        <div className="mx-auto max-w-7xl px-6">
          
          <h1 className="mt-3 font-display text-4xl font-bold text-[#141414]">Perlengkapan Ibadah & Oleh-oleh</h1>
          <div className="mt-6 flex flex-wrap gap-2">
            <button onClick={() => setCat("")} data-testid="shop-cat-all"
              className={`rounded-full px-4 py-2 text-sm ${!cat ? "bg-[#141414] text-white" : "border border-[#E5DEC9]"}`}>Semua</button>
            {data?.categories?.map((c) => (
              <button key={c.slug} onClick={() => setCat(c.slug)} data-testid={`shop-cat-${c.slug}`}
                className={`rounded-full px-4 py-2 text-sm ${cat === c.slug ? "bg-[#141414] text-white" : "border border-[#E5DEC9]"}`}>{c.name}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 lg:grid-cols-[1fr_320px]">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="shop-grid">
          {data === null ? <Loading /> : data.items.length === 0 ? <Empty label="Belum ada produk." /> :
            data.items.map((p, i) => (
              <div key={p.id} className="card-hover reveal overflow-hidden rounded-2xl border border-[#E5DEC9] bg-white" style={{ animationDelay: `${i * 60}ms` }}>
                <Link to={`/toko/${p.slug}`}><img src={p.image} alt={p.name} className="h-48 w-full object-cover" loading="lazy" /></Link>
                <div className="p-5">
                  <h3 className="font-display text-base font-semibold text-[#121212]">{p.name}</h3>
                  <div className="mt-2 text-lg font-extrabold text-[#141414]">{rupiah(p.price)}</div>
                  <div className="text-xs text-[#8A867C]">Stok: {p.stock}</div>
                  <button onClick={() => add(p)} data-testid={`shop-add-${p.slug}`} disabled={p.stock <= 0}
                    className="btn-gold mt-4 flex w-full items-center justify-center gap-2 rounded-xl py-2 text-sm font-bold disabled:opacity-50">
                    <ShoppingCart size={15} /> Tambah
                  </button>
                </div>
              </div>))}
        </div>
        <aside className="h-fit rounded-2xl border border-[#E5DEC9] bg-white p-6 lg:sticky lg:top-28" data-testid="cart-panel">
          <div className="eyebrow">Keranjang</div>
          {cart.length === 0 ? <p className="mt-4 text-sm text-[#8A867C]">Keranjang masih kosong.</p> : (
            <div className="mt-4 space-y-3">
              {cart.map((c) => (
                <div key={c.id} className="flex items-start justify-between gap-2 border-b border-[#F4EFE6] pb-2 text-sm">
                  <div><div className="font-semibold">{c.name}</div><div className="text-xs text-[#8A867C]">{c.qty} × {rupiah(c.price)}</div></div>
                  <button onClick={() => remove(c.id)} data-testid={`cart-remove-${c.id}`} className="text-[#B91C1C]"><Trash2 size={15} /></button>
                </div>))}
              <div className="flex justify-between pt-2 font-bold text-[#141414]"><span>Total</span><span data-testid="cart-total">{rupiah(total)}</span></div>
              <div className="rounded-lg bg-[#F4EFE6] p-3 text-xs text-[#8C6D1F]">Gratis ongkir untuk semua pesanan. Pembayaran manual (transfer).</div>
              <Link to="/checkout" data-testid="cart-checkout-btn"
                className="btn-gold block rounded-xl py-3 text-center text-sm font-bold">Checkout</Link>
            </div>)}
        </aside>
      </div>
    </SiteLayout>
  );
}

export function TokoDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/public/shop/${slug}`).then((r) => setP(r.data)).catch((e) => setErr(apiError(e))); }, [slug]);
  if (err) return <SiteLayout><Empty label={err} /></SiteLayout>;
  if (!p) return <SiteLayout><Loading /></SiteLayout>;
  return (
    <SiteLayout>
      <div className="mx-auto grid max-w-6xl gap-10 px-6 py-16 lg:grid-cols-2">
        <img src={p.image} alt={p.name} className="h-96 w-full rounded-2xl object-cover" />
        <div>
          
          <h1 data-testid="shop-detail-title" className="mt-3 font-display text-3xl font-bold text-[#141414]">{p.name}</h1>
          <div className="mt-4 text-2xl font-extrabold text-[#141414]">{rupiah(p.price)}</div>
          <p className="mt-4 text-[#4A4844]">{p.description}</p>
          <p className="mt-3 text-sm text-[#8A867C]">Stok tersedia: {p.stock}</p>
          <Link to="/toko" className="btn-gold mt-8 inline-block rounded-xl px-6 py-3 text-sm font-bold">Kembali ke Toko</Link>
        </div>
      </div>
    </SiteLayout>
  );
}

export function Artikel() {
  const [posts, setPosts] = useState(null);
  const load = useCallback(() => {
    api.get("/public/posts").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <SiteLayout>
      <div className="mx-auto max-w-7xl px-6 py-16">
        <SectionTitle overline="Artikel & Panduan" title="Panduan Ibadah dan Persiapan Perjalanan" />
        <div className="mt-12 grid gap-7 md:grid-cols-3" data-testid="artikel-grid">
          {posts === null ? <Loading /> : posts.length === 0 ? <Empty label="Belum ada artikel." /> :
            posts.map((a, i) => (
              <Link key={a.id} to={`/artikel/${a.slug}`} data-testid={`artikel-card-${a.slug}`}
                className="card-hover reveal overflow-hidden rounded-2xl border border-[#E5DEC9] bg-white" style={{ animationDelay: `${i * 70}ms` }}>
                <img src={a.cover_image} alt={a.title} className="h-48 w-full object-cover" loading="lazy" />
                <div className="p-6">
                  <div className="eyebrow">{a.category}</div>
                  <h3 className="mt-2 font-display text-lg font-semibold">{a.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-[#4A4844]">{a.excerpt}</p>
                  <div className="mt-3 text-xs text-[#8A867C]">{tanggal(a.published_at)}</div>
                </div>
              </Link>))}
        </div>
      </div>
    </SiteLayout>
  );
}

export function ArtikelDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/public/posts/${slug}`).then((r) => setA(r.data)).catch((e) => setErr(apiError(e))); }, [slug]);
  if (err) return <SiteLayout><Empty label={err} /></SiteLayout>;
  if (!a) return <SiteLayout><Loading /></SiteLayout>;
  return (
    <SiteLayout>
      <article className="mx-auto max-w-3xl px-6 py-16">
        <div className="eyebrow">{a.category}</div>
        <h1 data-testid="artikel-title" className="mt-3 font-display text-3xl font-bold text-[#141414] lg:text-4xl">{a.title}</h1>
        <div className="mt-3 text-sm text-[#8A867C]">{a.author_name} · {tanggal(a.published_at)}</div>
        <img src={a.cover_image} alt={a.title} className="mt-8 h-80 w-full rounded-2xl object-cover" />
        <div className="mt-8 whitespace-pre-line leading-relaxed text-[#4A4844]">{a.content}</div>
      </article>
    </SiteLayout>
  );
}

export function TentangKami() {
  const [page, setPage] = useState(null);
  const load = useCallback(() => {
    api.get("/public/pages/tentang-kami").then((r) => setPage(r.data)).catch(() => setPage(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <SiteLayout>
      <div className="relative h-72 overflow-hidden bg-[#0A0A0A]">
        <img src="https://images.unsplash.com/photo-1723640321874-c2bd2cead914?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxtZWNjYSUyMG1lZGluYSUyMG1vc3F1ZSUyMGthYWJhJTIwaXNsYW1pYyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODcxMzYwMzd8MA&ixlib=rb-4.1.0&q=85"
          alt="Tentang Kami" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="relative mx-auto flex h-full max-w-7xl items-end px-6 pb-10">
          <h1 className="font-display text-4xl font-bold text-white">Tentang Kami</h1>
        </div>
      </div>
      <div className="mx-auto max-w-4xl space-y-10 px-6 py-16" data-testid="tentang-sections">
        {page === null ? <Loading /> : page === false ? <Empty label="Halaman belum tersedia." /> :
          page.sections?.map((s, i) => (
            <section key={s.id} className="reveal" style={{ animationDelay: `${i * 90}ms` }}>
              <h2 className="font-display text-2xl font-semibold text-[#141414]">{s.heading}</h2>
              
              <p className="text-[#4A4844]">{s.body}</p>
            </section>))}
      </div>
    </SiteLayout>
  );
}

export function CekBooking() {
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setRes(null); setBusy(true);
    try {
      const { data } = await api.get("/public/check-booking", { params: { code, email } });
      setRes(data);
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };
  return (
    <SiteLayout>
      <div className="mx-auto max-w-3xl px-6 py-16">
        <SectionTitle overline="Cek Booking" title="Lacak Status Pendaftaran Anda"
          subtitle="Masukkan kode booking dan email yang Anda gunakan saat pendaftaran." />
        <form onSubmit={submit} className="mt-10 space-y-4 rounded-2xl border border-[#E5DEC9] bg-white p-7 luxury-shadow">
          <input value={code} onChange={(e) => setCode(e.target.value)} required placeholder="Kode Booking (mis. RSH-DEMO01)"
            data-testid="booking-code-input" className="w-full rounded-xl border border-[#E5DEC9] px-4 py-3 text-sm outline-none focus:border-[#C5A028]" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} required type="email" placeholder="Email pendaftaran"
            data-testid="booking-email-input" className="w-full rounded-xl border border-[#E5DEC9] px-4 py-3 text-sm outline-none focus:border-[#C5A028]" />
          <button disabled={busy} data-testid="booking-submit-btn" className="btn-gold flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold">
            <Search size={16} /> {busy ? "Mencari..." : "Cek Booking"}
          </button>
          {err && <p data-testid="booking-error" className="text-sm text-[#B91C1C]">{err}</p>}
        </form>
        {res && (
          <div data-testid="booking-result" className="mt-8 rounded-2xl border border-[#E5DEC9] bg-white p-7">
            <div className="eyebrow">Kode {res.booking_code}</div>
            <h3 className="mt-2 font-display text-xl font-semibold text-[#141414]">{res.product_name}</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
              <div>Nama: <b>{res.contact_name}</b></div>
              <div>Jumlah jamaah: <b>{res.pax}</b></div>
              <div>Total: <b>{rupiah(res.total_amount)}</b></div>
              <div>Sudah dibayar: <b>{rupiah(res.paid_amount)}</b></div>
              <div>Status: <b className="capitalize">{String(res.status).replace(/_/g, " ")}</b></div>
            </div>
            <div className="mt-5">
              <div className="eyebrow">Jamaah</div>
              <ul className="mt-2 space-y-1 text-sm">
                {res.pilgrims?.map((p, i) => <li key={p.id || `${p.full_name}-${i}`}>{p.full_name} — <span className="capitalize">{String(p.status).replace(/_/g, " ")}</span></li>)}
              </ul>
            </div>
          </div>)}
      </div>
    </SiteLayout>
  );
}

export function Kontak() {
  const [s, setS] = useState({});
  const [branches, setBranches] = useState([]);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const load = useCallback(() => {
    api.get("/public/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/public/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const term = q.trim().toLowerCase();
  const match = (t) => !term || (t || "").toLowerCase().includes(term);

  // Filter tree: keep any branch/subbranch/consultant that matches (or any child matches)
  const filtered = branches.map((b) => {
    const subs = (b.sub_branches || []).map((sb) => {
      const cons = (sb.consultants || []).filter((c) =>
        match(c.full_name) || match(c.code) || match(c.phone));
      const sbMatch = match(sb.name) || match(sb.city) || match(sb.phone);
      return { ...sb, consultants: cons, __show: sbMatch || cons.length > 0 };
    }).filter((sb) => sb.__show || !term);
    const bMatch = match(b.name) || match(b.city) || match(b.phone);
    return { ...b, sub_branches: subs, __show: bMatch || subs.length > 0 || !term };
  }).filter((b) => b.__show);

  const totalConsultants = branches.reduce(
    (n, b) => n + (b.sub_branches || []).reduce(
      (m, sb) => m + (sb.consultants || []).length, 0), 0);
  const totalSubs = branches.reduce((n, b) => n + (b.sub_branches || []).length, 0);

  const toggle = (id) => setExpanded((x) => ({ ...x, [id]: !x[id] }));

  return (
    <SiteLayout>
      <div className="mx-auto max-w-6xl px-6 py-16">
        <SectionTitle overline="Hubungi Kami" title="Kami Siap Membantu Perjalanan Ibadah Anda" />
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[[Phone, "Telepon", s.phone], [Mail, "Email", s.email], [MapPin, "Kantor Pusat", s.address]].map(([Icon, t, v]) => (
            <div key={t} className="rounded-2xl border border-[#E5DEC9] bg-white p-6">
              <Icon className="text-[#C5A028]" size={20} />
              <div className="mt-3 eyebrow">{t}</div>
              <div className="mt-1 text-sm font-semibold text-[#121212]">{v || "-"}</div>
            </div>))}
        </div>
        <a href={`https://wa.me/${s.whatsapp || ""}`} target="_blank" rel="noreferrer" data-testid="kontak-whatsapp-btn"
          className="btn-gold mt-8 inline-block rounded-xl px-7 py-3 text-sm font-bold">Chat WhatsApp Sekarang</a>

        <div className="mt-14">
          <SectionTitle overline="Jaringan Kami"
            title={`${branches.length} Cabang · ${totalSubs} Cabang Pembantu · ${totalConsultants} Umrah Consultant`} />

          <div className="mt-6 flex items-center gap-3 rounded-2xl border border-[#E5DEC9] bg-white p-3">
            <Search size={16} className="text-[#8A867C]" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Cari cabang, cabang pembantu, kota, atau nama Umrah Consultant..."
              data-testid="kontak-search"
              className="w-full bg-transparent text-sm outline-none" />
            {q && (
              <button onClick={() => setQ("")} className="text-xs font-semibold text-[#C5A028]">Reset</button>
            )}
          </div>

          <div className="mt-6 space-y-4" data-testid="branch-list">
            {filtered.length === 0 && (
              <div className="rounded-2xl border border-[#E5DEC9] bg-white p-8 text-center text-sm text-[#8A867C]">
                Tidak ada hasil untuk "{q}".
              </div>
            )}
            {filtered.map((b) => {
              const open = expanded[b.id] !== false;  // default open
              return (
                <div key={b.id} className="overflow-hidden rounded-2xl border border-[#E5DEC9] bg-white">
                  <button onClick={() => toggle(b.id)} data-testid={`branch-toggle-${b.id}`}
                    className="flex w-full items-center justify-between px-6 py-4 text-left hover:bg-[#FBF9F4]">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-[#C5A028]">Cabang</div>
                      <div className="mt-1 font-display text-lg font-bold text-[#141414]">{b.name}</div>
                      <div className="mt-1 text-xs text-[#8A867C]">{b.city} · {b.phone}</div>
                    </div>
                    {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  {open && (
                    <div className="border-t border-[#F4EFE6] px-6 py-4">
                      {b.sub_branches.length === 0 && (
                        <div className="text-sm text-[#8A867C]">Belum ada Cabang Pembantu.</div>
                      )}
                      <div className="space-y-3">
                        {b.sub_branches.map((sb) => (
                          <div key={sb.id} className="rounded-xl bg-[#FBF9F4] p-4" data-testid={`subbranch-${sb.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <div className="text-xs uppercase tracking-wider text-[#8A867C]">Cabang Pembantu</div>
                                <div className="mt-0.5 text-sm font-bold text-[#141414]">{sb.name}</div>
                                <div className="text-xs text-[#8A867C]">{sb.city} · {sb.phone}</div>
                              </div>
                              <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#4A4844]">
                                {sb.consultants.length} Consultant
                              </span>
                            </div>
                            {sb.consultants.length > 0 && (
                              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                {sb.consultants.map((c) => (
                                  <div key={c.id} className="rounded-lg border border-[#E5DEC9] bg-white p-3" data-testid={`consultant-${c.id}`}>
                                    <div className="text-sm font-bold text-[#141414]">{c.full_name}</div>
                                    <div className="mt-0.5 text-xs text-[#8A867C]">{c.code}{c.phone ? ` · ${c.phone}` : ""}</div>
                                    {c.phone && (
                                      <a href={`https://wa.me/${c.phone.replace(/\D/g, "")}`}
                                        target="_blank" rel="noreferrer"
                                        className="mt-2 inline-block text-xs font-bold text-[#C5A028] hover:underline">
                                        Chat WhatsApp →
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}
